import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  MergeType as MergeIcon,
  CheckCircle as ApproveIcon,
  Cancel as DenyIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { mergeRequestsApi } from '../../api';
import type { GlobalMergeRequestDetails, GlobalMergeResult } from '../../types';
import { formatRelativeTime } from '../../utils';

export interface GlobalMergeReviewDialogProps {
  open: boolean;
  globalRequestId: string | null;
  onClose: () => void;
  onApproved?: (result: GlobalMergeResult) => void;
  onDenied?: () => void;
}

/**
 * A dialog for reviewing and approving/denying incoming global merge requests.
 * This is shown to the TARGET user (the one receiving the squares).
 */
export const GlobalMergeReviewDialog: React.FC<GlobalMergeReviewDialogProps> = ({
  open,
  globalRequestId,
  onClose,
  onApproved,
  onDenied,
}) => {
  const [details, setDetails] = useState<GlobalMergeRequestDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDenyReason, setShowDenyReason] = useState(false);
  const [denyReason, setDenyReason] = useState('');

  // Fetch request details when dialog opens
  const fetchDetails = useCallback(async () => {
    if (!globalRequestId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await mergeRequestsApi.getGlobalDetails(globalRequestId);
      setDetails(data);
    } catch (_err) {
      setError('Failed to load merge request details');
      setDetails(null);
    } finally {
      setLoading(false);
    }
  }, [globalRequestId]);

  useEffect(() => {
    if (open && globalRequestId) {
      fetchDetails();
    } else {
      setDetails(null);
      setShowDenyReason(false);
      setDenyReason('');
    }
  }, [open, globalRequestId, fetchDetails]);

  const handleClose = () => {
    setError(null);
    setDetails(null);
    setShowDenyReason(false);
    setDenyReason('');
    onClose();
  };

  const handleApprove = async () => {
    if (!globalRequestId) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await mergeRequestsApi.approveGlobal(globalRequestId);
      onApproved?.(result);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve merge request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    if (!globalRequestId) return;

    if (!showDenyReason) {
      setShowDenyReason(true);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await mergeRequestsApi.denyGlobal(globalRequestId, { reason: denyReason || undefined });
      onDenied?.();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny merge request');
    } finally {
      setSubmitting(false);
    }
  };

  const totalSquares = details?.totalSquares ?? 0;
  const totalLedger = details?.totalLedgerAmount ?? 0;
  const boardCount = details?.boardDetails?.length ?? 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MergeIcon color="primary" />
          <Typography variant="h6" component="span">
            Review Merge Request
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !details ? (
          <Alert severity="warning">
            <Typography variant="body2">
              Merge request not found or has expired.
            </Typography>
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>{details.requestedBy?.displayName || 'A manager'}</strong> wants to merge{' '}
                <strong>{details.offlinePlayer?.displayName || 'an offline player'}</strong> into your account.
                All associated squares will be transferred to you.
              </Typography>
            </Alert>

            {/* Expiration warning */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                Expires {formatRelativeTime(details.expiresAt)}
              </Typography>
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Boards affected ({boardCount}):
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Board</TableCell>
                    <TableCell align="right">Squares</TableCell>
                    <TableCell align="right">Ledger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.boardDetails.map((board) => (
                    <TableRow key={board.boardId}>
                      <TableCell>{board.boardName}</TableCell>
                      <TableCell align="right">{board.squareCount}</TableCell>
                      <TableCell align="right">{board.ledgerEntries}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">{totalSquares}</TableCell>
                    <TableCell align="right">{totalLedger}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                p: 2,
                bgcolor: 'success.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.200',
                mb: 2,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                If you approve:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {totalSquares} square{totalSquares !== 1 ? 's' : ''} will be transferred to your account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • You'll be added to any boards you're not already on
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • The offline player "{details.offlinePlayer?.displayName}" will be removed
              </Typography>
            </Box>

            {showDenyReason && (
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Reason for denial (optional)"
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                placeholder="Let the manager know why you're declining..."
                sx={{ mb: 2 }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        {details && (
          <>
            <Button
              onClick={handleDeny}
              color="error"
              variant="outlined"
              disabled={submitting}
              startIcon={submitting && showDenyReason ? <CircularProgress size={18} /> : <DenyIcon />}
            >
              {showDenyReason ? 'Confirm Deny' : 'Deny'}
            </Button>
            <Button
              onClick={handleApprove}
              variant="contained"
              color="success"
              disabled={submitting || showDenyReason}
              startIcon={submitting && !showDenyReason ? <CircularProgress size={18} /> : <ApproveIcon />}
            >
              Approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GlobalMergeReviewDialog;
